import axios from 'axios'
import { useEffect, useRef, useState } from 'react'
import { CloseIcon } from 'src/components/icons/CloseIcon'
import ArcaMarkerIcon from 'src/components/icons/store/ArcaMarkerIcon'
import { useGoogleMapsContext } from 'src/contexts/google-maps-context'
import { useStoreContext } from 'src/contexts/store-context'
import { useSaveFavouriteStore } from 'src/sdk/store/useSaveFavouriteStore'
import {
  getIsStoreOpen,
  getStoreAddress,
  getStoreName,
  useSlidingAnimation,
} from './utils/utils'

interface Props {
  isSidebar: boolean
  setIsSidebar: React.Dispatch<React.SetStateAction<boolean>>
}

export type Days =
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'
  | 'sunday'

export type PickupPoint = {
  data: {
    address: {
      line1: string
      city: string
      region: string
      postalCode: string
    }
    id: string
    hours: {
      [key in Days]: {
        openIntervals: Array<{
          start: string
          end: string
        }>
      }
    }
    yextDisplayCoordinate: {
      latitude: number
      longitude: number
    }
  }
}

export type PickupData = {
  address: {
    city: string
    street: string
    state: string
  }
  geoCoordinates: [number, number]
  id: string
  name: string
  postalCode: string
  sellerId: string | null
  vtexSellerId: string
}

const StorePickerSidebar = ({ isSidebar, setIsSidebar }: Props) => {
  const [pickupPointsList, setPickupPointsList] = useState<PickupPoint[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const { placesAPI } = useGoogleMapsContext()
  const { className } = useSlidingAnimation(isSidebar)
  const { favouriteStore } = useStoreContext()
  const { handleSaveFavouriteStore } = useSaveFavouriteStore()

  const getPickupList = async (latitude: number, longitude: number) => {
    try {
      setIsLoading(true)
      setError(null)
      const { data: yextResponse } = await axios.post(
        `/api/store/getStoreList`,
        {
          lat: latitude,
          lng: longitude,
          // 30 KM:
          radius: 30000,
        }
      )
      setPickupPointsList(yextResponse.response.results)
    } catch (err) {
      setError(
        'Non è stato possibile recuperare i negozi presenti nella tua zona. Riprova.'
      )
    } finally {
      setIsLoading(false)
    }
  }

  const saveFavouriteStore = async (store: PickupData) => {
    setIsLoading(true)
    try {
      await handleSaveFavouriteStore(store)
    } finally {
      setIsSidebar(false)
    }
  }

  useEffect(() => {
    if (favouriteStore && favouriteStore.geoCoordinates) {
      getPickupList(
        favouriteStore.geoCoordinates[1],
        favouriteStore.geoCoordinates[0]
      )
    }
  }, [favouriteStore])

  useEffect(() => {
    const eventHandler = async () => {
      const place = autocomplete.getPlace()
      if (!place.geometry) {
        return
      }

      const latitude = place.geometry.location.lat()
      const longitude = place.geometry.location.lng()

      await getPickupList(latitude, longitude)
    }

    const autocomplete = new placesAPI.Autocomplete(searchRef.current)
    autocomplete?.setFields(['geometry.location'])

    autocomplete?.addListener('place_changed', eventHandler)
  }, [])

  return (
    <div className="store-sidebar fixed top-0 bottom-0 left-0 right-0 bg-opacity-30 bg-black flex z-50 font-sans">
      <div
        className={`flex flex-col rounded-r-2xl bg-white w-[380px] max-w-[90%] p-4 md:p-8 transition-transform -translate-x-full ${className}`}
      >
        <header className="flex flex-col gap-4 border-b border-b-gray-400 pb-6">
          <div className="flex justify-between gap-4 items-center">
            <h3 className="font-semibold">Il tuo negozio preferito</h3>
            <button
              onClick={() => setIsSidebar(false)}
              aria-label="Chiudi sidebar"
              className="bg-transparent outline-none p-0 cursor-pointer flex items-center justify-center"
            >
              <CloseIcon className="shrink-0" />
            </button>
          </div>
          <input
            disabled={isLoading}
            ref={searchRef}
            type="text"
            autoComplete="false"
            placeholder="Inserisci l'indirizzo o la città"
            name="place-input"
            className="p-3 border rounded-md text-gray-900 border-gray-500 text-sm disabled:cursor-not-allowed"
          />
        </header>
        <main className="flex flex-col overflow-y-auto h-full">
          {isLoading ? (
            <div className="flex items-center justify-center h-full w-full">
              <div className="animate-spin w-12 h-12 border-4 border-black rounded-full"></div>
            </div>
          ) : error ? (
            <div className="text-red-500 text-center flex items-center justify-center h-full w-full">
              {error}
            </div>
          ) : (
            pickupPointsList.map((el: PickupPoint) => {
              const name = getStoreName(el)
              const address = getStoreAddress(el)
              const status = getIsStoreOpen(el)
              const isSelected = el.data.id === favouriteStore?.id
              const store: PickupData = {
                address: {
                  city: el.data.address.city,
                  street: el.data.address.line1,
                  state: el.data.address.region,
                },
                geoCoordinates: [
                  el.data.yextDisplayCoordinate.longitude,
                  el.data.yextDisplayCoordinate.latitude,
                ],
                id: el.data.id,
                name: `Arcaplanet - ${el.data.address.city} ${el.data.address.line1}`,
                postalCode: el.data.address.postalCode,
                sellerId: null,
                vtexSellerId: `AGRI${el.data.id}`,
              }

              return (
                <div
                  className="py-5 flex flex-col gap-3 border-b border-b-gray-400 last:border-b-transparent"
                  key={el.data.id}
                >
                  <div className="flex flex-col gap-1">
                    {isSelected && (
                      <div className="bg-[#CDFBE7] text-[#08683F] font-semibold text-xs flex items-center justify-center p-2 rounded-md mx-auto">
                        Negozio selezionato
                      </div>
                    )}
                    <div className="flex gap-2 items-center">
                      <ArcaMarkerIcon className="shrink-0" />
                      <div className="font-semibold text-sm">{name}</div>
                    </div>
                  </div>
                  <div className="flex flex-col text-sm">
                    <div className={`${status.color} font-semibold`}>
                      {status.label}
                    </div>
                    <div>{address}</div>
                  </div>
                  <button
                    onClick={() => saveFavouriteStore(store)}
                    className="rounded-md text-sm bg-yellow outline-none cursor-pointer flex items-center justify-center px-4 py-2 font-semibold w-fit mt-1"
                  >
                    Seleziona lo store
                  </button>
                </div>
              )
            })
          )}
        </main>
      </div>
    </div>
  )
}

export default StorePickerSidebar
